import React, { useState, useRef, useEffect } from 'react';
import StarRating from 'components/review/StarRating';
import MediaViewer from 'components/common/MediaViewer';
import 'assets/scss/pages/Review.scss';
import { useLocation } from 'react-router-dom';

const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return `${date.getFullYear()}.${String(date.getMonth() + 1).padStart(2, '0')}.${String(date.getDate()).padStart(2, '0')}`;
};

// user_name의 첫 글자만 노출하고 나머지는 ** 처리 (예: 신소진 -> 신**, jane -> j**)
const maskUserName = (name) => {
    return `${name.charAt(0)}**`;
};

const ReviewCard = ({ review, onEdit, onDelete }) => {
  const pathname = useLocation().pathname;
    const rawName = review?.user_name || review?.nickname || '';
    const displayName = maskUserName(rawName);
    const avatarInitial = rawName ? rawName.charAt(0) : '?';
    const [menuOpen, setMenuOpen] = useState(false);
    const [lightboxImage, setLightboxImage] = useState(null);
    const menuRef = useRef(null);
    const hasActions = onEdit || onDelete;

    useEffect(() => {
        const handleOutsideClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setMenuOpen(false);
            }
        };
        if (menuOpen) {
            document.addEventListener('mousedown', handleOutsideClick);
        }
        return () => document.removeEventListener('mousedown', handleOutsideClick);
    }, [menuOpen]);

    const handleEdit = () => {
        setMenuOpen(false);
        onEdit?.(review);
    };

    const handleDelete = () => {
        setMenuOpen(false);
        onDelete?.(review.id);
    };

    return (
        <div className="review-card">
            <div className="review-card__header">
                <div className="review-card__profile">
                    {review.user?.profile_image ? (
                        <img
                            src={review.user.profile_image}
                            alt={displayName}
                            className="review-card__avatar"
                        />
                    ) : (
                        <div className="review-card__avatar review-card__avatar--placeholder">
                            {avatarInitial}
                        </div>
                    )}
                    <div className="review-card__user-info">
                        <span className="review-card__nickname">{pathname.includes('mypage') ? review.user_name:displayName}</span>
                        <div className="review-card__meta">
                            <StarRating rating={review.rating} size={14} />
                            <span className="review-card__date">{formatDate(review.created_at)}</span>
                            {review.is_edited && (
                                <span className="review-card__edited">수정됨</span>
                            )}
                        </div>
                    </div>
                </div>

                {hasActions && (
                    <div className="review-card__menu" ref={menuRef}>
                        <button
                            type="button"
                            className="review-card__menu-btn"
                            onClick={() => setMenuOpen((prev) => !prev)}
                            aria-label="더보기 메뉴"
                            aria-expanded={menuOpen}
                        >
                            <span className="review-card__dots">···</span>
                        </button>
                        {menuOpen && (
                            <ul className="review-card__dropdown" role="menu">
                                {onEdit && (
                                    <li role="menuitem">
                                        <button type="button" onClick={handleEdit}>수정</button>
                                    </li>
                                )}
                                {onDelete && (
                                    <li role="menuitem">
                                        <button type="button" onClick={handleDelete} className="review-card__dropdown-delete">삭제</button>
                                    </li>
                                )}
                            </ul>
                        )}
                    </div>
                )}
            </div>

            <p className="review-card__content">{review.content}</p>

            {review.images?.length > 0 && (
                <div className="review-card__images">
                    {review.images.map((img) => (
                        <button
                            key={img.id}
                            type="button"
                            className="review-card__image-btn"
                            onClick={() => setLightboxImage(img.image)}
                            aria-label="이미지 크게 보기"
                        >
                            <img
                                src={img.image}
                                alt="리뷰 이미지"
                                className="review-card__image"
                            />
                        </button>
                    ))}
                </div>
            )}

            {review.reply && (
                <div className="review-card__reply">
                    <div className="review-card__reply-header">
                        <span className="review-card__reply-author">{review.reply.author_name || '사업자'}</span>
                        <span className="review-card__reply-date">{formatDate(review.reply.created_at)}</span>
                    </div>
                    <p className="review-card__reply-content">{review.reply.content}</p>
                </div>
            )}

            {/* 저장 기능은 채팅 전용이므로 onDownload 를 넘기지 않는다. */}
            {lightboxImage && (
                <MediaViewer
                    media={[{ url: lightboxImage, type: 'image' }]}
                    title="리뷰 이미지"
                    onClose={() => setLightboxImage(null)}
                />
            )}
        </div>
    );
};

export default ReviewCard;
